/**
 * Coordinate Transform — PDF ↔ Map Registration
 *
 * Coordinate pipeline:
 *   PDF points (origin bottom-left) → normalized [0,1] → EPSG:3857 meters → WGS84 [lng,lat]
 *
 * Solvers:
 *   - Similarity (Procrustes): 2-3 anchors → rotation + uniform scale + translation
 *   - Affine: ≥4 anchors → independent scale/shear/rotation per axis
 *
 * Uses ml-matrix for least-squares solving.
 */

import { Matrix, solve } from 'ml-matrix';
import type { AnchorPair, LayoutTransform, AnchorResidual } from './types';

// ── EPSG:3857 (Web Mercator) ↔ WGS84 ─────────────────────

const EARTH_RADIUS = 6378137; // WGS84 semi-major axis in meters

export function lngLatToMercator(lng: number, lat: number): [number, number] {
  const x = (lng * Math.PI / 180) * EARTH_RADIUS;
  const y = Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI / 180) / 2)) * EARTH_RADIUS;
  return [x, y];
}

export function mercatorToLngLat(mx: number, my: number): [number, number] {
  const lng = (mx / EARTH_RADIUS) * (180 / Math.PI);
  const lat = (2 * Math.atan(Math.exp(my / EARTH_RADIUS)) - Math.PI / 2) * (180 / Math.PI);
  return [lng, lat];
}

// ── Similarity Transform (Procrustes, ≥2 anchors) ────────

/**
 * Solve a 2D similarity transform: [a, b, tx, c, d, ty]
 * Maps PDF points → Mercator meters.
 *
 * Model: [X]   [a  -b] [x]   [tx]
 *        [Y] = [b   a] [y] + [ty]
 *
 * Solved via least squares (ATA)^-1 ATb.
 */
function solveSimilarity(
  srcPts: [number, number][],
  dstPts: [number, number][],
): number[] {
  const n = srcPts.length;
  // Build A matrix (2n × 4) and b vector (2n × 1)
  const A = new Array(2 * n);
  const b = new Array(2 * n);

  for (let i = 0; i < n; i++) {
    const [sx, sy] = srcPts[i];
    const [dx, dy] = dstPts[i];
    A[2 * i] = [sx, -sy, 1, 0];
    A[2 * i + 1] = [sy, sx, 0, 1];
    b[2 * i] = dx;
    b[2 * i + 1] = dy;
  }

  const matA = new Matrix(A);
  const vecB = Matrix.columnVector(b);

  // Solve via least squares: Ax = b
  const solution = solve(matA, vecB);

  const a = solution.get(0, 0);
  const bVal = solution.get(1, 0);
  const tx = solution.get(2, 0);
  const ty = solution.get(3, 0);

  // Return as affine 2×3: [a, -b, tx, b, a, ty]
  return [a, -bVal, tx, bVal, a, ty];
}

// ── Affine Transform (≥3 anchors) ─────────────────────────

/**
 * Solve a 2D affine transform: [a, b, tx, c, d, ty]
 * Maps PDF points → Mercator meters.
 *
 * Model: [X]   [a  b] [x]   [tx]
 *        [Y] = [c  d] [y] + [ty]
 *
 * Unlike similarity (which constrains a=d, b=-c for rotation+uniform-scale),
 * affine allows independent scaling, shear, and rotation in each axis.
 * This is the correct model for flat engineering drawings mapped to geographic space.
 */
function solveAffine(
  srcPts: [number, number][],
  dstPts: [number, number][],
): number[] {
  const n = srcPts.length;
  const A = new Array(2 * n);
  const b = new Array(2 * n);

  for (let i = 0; i < n; i++) {
    const [sx, sy] = srcPts[i];
    const [dx, dy] = dstPts[i];
    A[2 * i] = [sx, sy, 1, 0, 0, 0];
    A[2 * i + 1] = [0, 0, 0, sx, sy, 1];
    b[2 * i] = dx;
    b[2 * i + 1] = dy;
  }

  const matA = new Matrix(A);
  const vecB = Matrix.columnVector(b);
  const solution = solve(matA, vecB);

  return [
    solution.get(0, 0), // a
    solution.get(1, 0), // b
    solution.get(2, 0), // tx
    solution.get(3, 0), // c
    solution.get(4, 0), // d
    solution.get(5, 0), // ty
  ];
}

// ── Transform Application ─────────────────────────────────

function applyAffine(matrix: number[], px: number, py: number): [number, number] {
  const [a, b, tx, c, d, ty] = matrix;
  return [a * px + b * py + tx, c * px + d * py + ty];
}

export function transformPoint(
  transform: LayoutTransform,
  pdfX: number,
  pdfY: number,
): [number, number] {
  // Both similarity and affine use the same [a,b,tx,c,d,ty] format
  const [mx, my] = applyAffine(transform.matrix, pdfX, pdfY);
  return mercatorToLngLat(mx, my);
}

/**
 * Inverse affine: map [lng, lat] → PDF [pdfX, pdfY].
 * Inverts the [a, b, tx, c, d, ty] matrix.
 */
function inverseAffine(matrix: number[], mx: number, my: number): [number, number] {
  const [a, b, tx, c, d, ty] = matrix;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-12) return [0, 0]; // degenerate
  const px = (d * (mx - tx) - b * (my - ty)) / det;
  const py = (-c * (mx - tx) + a * (my - ty)) / det;
  return [px, py];
}

export function inverseTransformPoint(
  transform: LayoutTransform,
  lng: number,
  lat: number,
): [number, number] {
  const [mx, my] = lngLatToMercator(lng, lat);
  return inverseAffine(transform.matrix, mx, my);
}

// ── Residual Computation ──────────────────────────────────

function computeResiduals(
  anchors: AnchorPair[],
  matrix: number[],
  _type: 'similarity' | 'affine',
): { residuals: AnchorResidual[]; rmse: number } {
  const residuals: AnchorResidual[] = [];
  let sumSq = 0;

  for (const anchor of anchors) {
    const [predMx, predMy] = applyAffine(matrix, anchor.pdfX, anchor.pdfY);
    const [trueMx, trueMy] = lngLatToMercator(anchor.mapLng, anchor.mapLat);
    const dx = predMx - trueMx;
    const dy = predMy - trueMy;
    const errorMeters = Math.sqrt(dx * dx + dy * dy);
    residuals.push({ anchorId: anchor.id, errorMeters });
    sumSq += dx * dx + dy * dy;
  }

  const rmse = Math.sqrt(sumSq / anchors.length);
  return { residuals, rmse };
}

// ── Public API ────────────────────────────────────────────

/**
 * Compute the coordinate transform from PDF space to map space.
 *
 * - 2-3 anchors → similarity (rotation + uniform scale + translation)
 * - 4+ anchors → affine (independent scale/shear/rotation)
 *
 * Returns null if < 2 anchors.
 */
export function solveTransform(anchors: AnchorPair[]): LayoutTransform | null {
  if (anchors.length < 2) return null;

  // Prepare source (PDF) and destination (Mercator) point arrays
  const srcPts: [number, number][] = anchors.map((a) => [a.pdfX, a.pdfY]);
  const dstPts: [number, number][] = anchors.map((a) => lngLatToMercator(a.mapLng, a.mapLat));

  // Similarity (4 DOF): rotation + uniform scale + translation — best for 2-3 anchors
  // Affine (6 DOF): independent scale/shear/rotation — best for 4+ anchors
  // Both are appropriate for flat document → map registration.
  // Homography (8 DOF) is NOT used because engineering drawings are flat, not perspective.
  const type: 'similarity' | 'affine' = anchors.length >= 4 ? 'affine' : 'similarity';

  const matrix = type === 'similarity'
    ? solveSimilarity(srcPts, dstPts)
    : solveAffine(srcPts, dstPts);

  const { residuals, rmse } = computeResiduals(anchors, matrix, type);

  // Confidence heuristic
  const warnings: string[] = [];
  let confidence = 1.0;

  if (anchors.length === 2) {
    confidence *= 0.6;
    warnings.push('LOW_ANCHORS');
  } else if (anchors.length === 3) {
    confidence *= 0.8;
    warnings.push('LOW_ANCHORS');
  }

  if (rmse > 50) {
    confidence *= 0.3;
    warnings.push('HIGH_RMSE');
  } else if (rmse > 20) {
    confidence *= 0.6;
    warnings.push('HIGH_RMSE');
  } else if (rmse > 5) {
    confidence *= 0.85;
  }

  // Check for possible schematic (non-geographic) layout
  const mapBbox = {
    minLng: Math.min(...anchors.map((a) => a.mapLng)),
    maxLng: Math.max(...anchors.map((a) => a.mapLng)),
    minLat: Math.min(...anchors.map((a) => a.mapLat)),
    maxLat: Math.max(...anchors.map((a) => a.mapLat)),
  };
  const spanMeters = Math.sqrt(
    Math.pow((mapBbox.maxLng - mapBbox.minLng) * 111000, 2) +
    Math.pow((mapBbox.maxLat - mapBbox.minLat) * 111000, 2),
  );
  if (spanMeters < 10) {
    warnings.push('POSSIBLE_SCHEMATIC');
    confidence *= 0.4;
  }

  confidence = Math.max(0, Math.min(1, confidence));

  return {
    type,
    matrix,
    residualMeters: rmse,
    anchorResiduals: residuals,
    confidence,
    warnings,
  };
}

/**
 * Transform a PDF point to map coordinates [lng, lat].
 * Returns null if no transform available.
 */
export function pdfToMap(
  transform: LayoutTransform | undefined,
  pdfX: number,
  pdfY: number,
): [number, number] | null {
  if (!transform) return null;
  return transformPoint(transform, pdfX, pdfY);
}

/**
 * Transform all text entities to GeoJSON features for map rendering.
 */
export function entitiesToGeoJSON(
  entities: Array<{ id: string; cx: number; cy: number; normalizedText: string; text: string; classification?: { level: string; value: string } }>,
  transform: LayoutTransform,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const entity of entities) {
    const level = entity.classification?.level ?? 'unclassified';
    const value = entity.classification?.value ?? entity.normalizedText;

    const [lng, lat] = transformPoint(transform, entity.cx, entity.cy);

    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lng, lat] },
      properties: {
        entityId: entity.id,
        text: entity.normalizedText,
        originalText: entity.text,
        level,
        value,
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

/**
 * Compute the 4 corner coordinates of the page image for Mapbox image source.
 * Returns [TL, TR, BR, BL] as [lng, lat] pairs.
 */
export function pageImageCorners(
  pageWidthPt: number,
  pageHeightPt: number,
  transform: LayoutTransform,
): [[number, number], [number, number], [number, number], [number, number]] {
  // PDF coordinate space: origin = bottom-left
  // Top-left = (0, pageHeight), Top-right = (pageWidth, pageHeight)
  // Bottom-right = (pageWidth, 0), Bottom-left = (0, 0)
  const tl = transformPoint(transform, 0, pageHeightPt);
  const tr = transformPoint(transform, pageWidthPt, pageHeightPt);
  const br = transformPoint(transform, pageWidthPt, 0);
  const bl = transformPoint(transform, 0, 0);
  return [tl, tr, br, bl];
}
